/**
 * BrandProductsPage Component
 * Displays products for a specific brand
 */

import React, { useEffect, useState, useLayoutEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Container, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

import BrandProductList from '../components/BrandProductList';
import BrandUnderConstruction from '../components/BrandUnderConstruction';
import { getBrandProducts } from '../services/brandProductsService';

const BrandProductsPage = () => {
  const { brandId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [loading, setLoading] = useState(true);
  const [brand, setBrand] = useState(null);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);
  
  // Efecto para manejar el scroll
  useLayoutEffect(() => {
    // Usar requestAnimationFrame para asegurar que el DOM se ha actualizado
    const timeoutId = setTimeout(() => {
      window.requestAnimationFrame(() => {
        window.scrollTo({
          top: 0,
          behavior: location.state?.scrollBehavior || 'auto'
        });
      });
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [brandId, location.state]);
  
  // Efecto para reiniciar el estado de carga cuando cambia el brandId
  useEffect(() => {
    // Cada vez que cambie el brandId, resetear el estado para mostrar el loader
    setLoading(true);
    setBrand(null);
    setProducts([]);
    setError(null);
  }, [brandId]);
  
  useEffect(() => {
    const fetchBrandProducts = async () => {
      try {
        const { brand, products } = await getBrandProducts(brandId);
        
        if (!brand) {
          setError('NOT_FOUND');
          return;
        }
        
        setBrand(brand);
        setProducts(products);
      } catch (err) {
        console.error('Error fetching brand products:', err);
        setError('FETCH_ERROR');
      } finally {
        setLoading(false);
      }
    };
    
    fetchBrandProducts();
  }, [brandId, t]);

  const handleBackToBrands = () => {
    navigate('/');
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // Si la marca no se encuentra o hay un error de carga, mostrar el componente BrandUnderConstruction
  if (error === 'NOT_FOUND' || !brand) {
    return <BrandUnderConstruction 
      onBackToBrands={handleBackToBrands}
      currentBrandId={brandId}
    />;
  }

  // Si hay un error de fetch pero tenemos la marca, mostrar un mensaje de error más específico
  if (error === 'FETCH_ERROR') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <Container maxWidth="lg" sx={{ padding: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" color="error" gutterBottom>
              {t('brands.errorTitle')}
            </Typography>
            <Typography variant="body1">
              {t('brands.errorLoading')}
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }
  
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <Container maxWidth="lg" sx={{ padding: { xs: 2, sm: 3, md: 4 } }}>
        <BrandProductList 
          brand={brand} 
          products={products}
          onBackToBrands={handleBackToBrands}
        />
      </Container>
    </Box>
  );
};

export default BrandProductsPage;
